<div class="justup-dialog">
  <vl-dialog-header-export
    [exporting]="exporting()"
    (close)="dialogRef.close()"
    (export)="exportExcel()"
  >
    Évolution prix de vente — {{ data.articleDenomination }}
  </vl-dialog-header-export>

  <div class="justup-dialog-content flex-col gap-16">
    <!-- ViewMode toggle -->
    <div class="flex-row justify-center">
      <vl-chips-toggle [elements]="viewModeElements" [(value)]="viewMode" size="small"/>
    </div>

    <!-- Stats cards -->
    <div class="flex-row gap-8 wrap">
      <vl-radio-card mode="display" color="valid" icon="euro"
                     [title]="'PU moyen'"
                     style="flex: 1; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ stats().puMoyen | number:'1.2-2' }} €</span>
          <span class="tiny" style="opacity: 0.7">/ {{ data.articleUniteVente || 'UV' }}</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display" color="secondary" icon="swap_vert"
                     [title]="'Min / Max'"
                     style="flex: 1; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ stats().puMin | number:'1.2-2' }} € <span class="tiny" style="opacity: 0.6">→</span> {{ stats().puMax | number:'1.2-2' }} €</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display" [color]="variationColor()" icon="trending_up"
                     [title]="'Variation'"
                     style="flex: 1; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="headline">
            @if (stats().variation > 0) { + }
            {{ stats().variation | number:'1.1-1' }}%
          </span>
          <span class="tiny" style="opacity: 0.7">1er → dernier</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display" [color]="margeColor()" icon="percent"
                     [title]="'Marge moyenne'"
                     style="flex: 1; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ stats().tauxMarge | number:'1.1-1' }}%</span>
          <span class="tiny" style="opacity: 0.7">{{ stats().margeFormatted }}</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display" color="basic" icon="receipt_long"
                     [title]="'Factures'"
                     style="flex: 1; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ stats().nbFactures }}</span>
          <span class="tiny" style="opacity: 0.7">{{ stats().qtyTotale | number:'1.0-0' }} {{ data.articleUniteVente || 'UV' }}</span>
        </div>
      </vl-radio-card>
    </div>

    @if (data.tiersId || data.articleId) {
      <div class="nc-section justup-card-aside container-background-reverse-alt p-12">
        <vl-nc-aside-block
          [tiersId]="data.tiersId"
          [articleId]="data.articleId"
          [showCostSummary]="true"
          [includeAll]="true"
          title="Non-conformités sur cet axe"
          data-testid="nc-aside-vente-dialog"/>
      </div>
    }

    @if (viewMode() === 'byFacture') {
      <!-- Header -->
      <div class="flex-row gap-8 p-8 align-center"
           style="padding-right: 12px; border-bottom: 1px solid var(--color-on-disabled)">
        <span class="shrink-0" style="width: 16px"></span>
        <span class="grow tiny on-background-alt"
              style="font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
          Facture
        </span>
        <span class="tiny on-background-alt shrink-0"
              matTooltip="Prix unitaire de vente moyen (pondéré par quantité)"
              style="min-width: 70px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
          PU
        </span>
        <span class="tiny on-background-alt shrink-0"
              matTooltip="Taux de marge = (CA − Coût) / CA"
              style="min-width: 50px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
          Marge
        </span>
        <span class="tiny on-background-alt shrink-0"
              matTooltip="Chiffre d'affaires HT"
              style="min-width: 80px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
          CA
        </span>
      </div>

      @for (f of sortedFactures(); track f.factureId) {
        <div>
          <div class="flex-row gap-8 p-8 align-center border-box border-8 cursor-pointer"
               style="border: 1px solid var(--color-outline)"
               (click)="toggle(f.factureId)">
            <mat-icon class="on-disabled shrink-0" style="font-size: 16px; width: 16px; height: 16px">
              {{ isOpen(f.factureId) ? 'expand_more' : 'chevron_right' }}
            </mat-icon>
            <div class="grow flex-col" style="min-width: 0">
              <span class="caption on-background-alt" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap">{{ f.numero }}</span>
              <span class="tiny on-disabled">{{ formatDate(f.dateKey) }} · {{ f.clientDenomination }}</span>
            </div>
            <span class="tiny on-background-alt shrink-0" style="min-width: 70px; text-align: right; font-weight: 600">
              {{ f.puVenteMoyen | number:'1.2-2' }} €
            </span>
            <span class="tiny shrink-0" [class]="margeClass(f.tauxMarge)" style="min-width: 50px; text-align: right; font-weight: 600">
              {{ f.tauxMarge | number:'1.0-0' }}%
            </span>
            <span class="tiny on-disabled shrink-0" style="min-width: 80px; text-align: right">
              {{ f.ca | number:'1.0-0' }} €
            </span>
          </div>

          @if (isOpen(f.factureId) && f.lots.length) {
            <div class="flex-col gap-2 mt-2 mb-2" style="margin-left: 24px">
              @for (lot of f.lots; track lot.lotNumero) {
                <div class="flex-row gap-12 p-8 border-on-disabled border-box border-8">
                  <div class="flex-col gap-4 grow" style="min-width: 0">
                    @if (lot.lotId) {
                      <vl-lot-card [lotId]="lot.lotId" mode="text"/>
                    } @else {
                      <span class="caption on-background-alt" style="font-weight: 600">
                        {{ lot.lotNumero || '— lot inconnu —' }}
                      </span>
                    }
                    <div class="flex-row gap-4 align-baseline">
                      <span class="tiny on-disabled" style="min-width: 60px">Vente</span>
                      <span class="tiny on-background-alt">{{ lot.qtyUV | number:'1.0-0' }} {{ lot.uniteVente }} × {{ lot.puVente | number:'1.2-2' }} €</span>
                    </div>
                    @if (lot.uniteGestion !== lot.uniteVente || lot.qtyUG !== lot.qtyUV) {
                      <div class="flex-row gap-4 align-baseline">
                        <span class="tiny on-disabled" style="min-width: 60px">Coût</span>
                        <span class="tiny on-background-alt">{{ lot.qtyUG | number:'1.0-0' }} {{ lot.uniteGestion }} × {{ lot.puCout | number:'1.2-2' }} €</span>
                      </div>
                    }
                  </div>
                  <div class="flex-col gap-2 shrink-0 align-end justify-center">
                    <span class="caption" [class]="margeClass(lot.tauxMarge)" style="font-weight: 700">
                      {{ lot.tauxMarge | number:'1.0-0' }}%
                    </span>
                    <span class="tiny on-disabled">Marge : {{ lot.marge | number:'1.0-0' }} €</span>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }
    } @else {
      <!-- Header mois -->
      <div class="flex-row gap-8 p-8 align-center"
           style="padding-right: 12px; border-bottom: 1px solid var(--color-on-disabled)">
        <span class="grow tiny on-background-alt"
              style="font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
          Mois
        </span>
        <span class="tiny on-background-alt shrink-0"
              style="min-width: 50px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
          Nb
        </span>
        <span class="tiny on-background-alt shrink-0"
              style="min-width: 70px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
          PU moy.
        </span>
        <span class="tiny on-background-alt shrink-0"
              style="min-width: 50px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
          Marge
        </span>
        <span class="tiny on-background-alt shrink-0"
              style="min-width: 80px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
          CA
        </span>
      </div>

      @for (m of monthRows(); track m.monthKey) {
        <div class="flex-row gap-8 p-8 align-center border-box border-8"
             style="border: 1px solid var(--color-outline)">
          <span class="grow caption on-background-alt" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
            {{ m.monthLabel }}
          </span>
          <span class="tiny on-disabled shrink-0" style="min-width: 50px; text-align: right">{{ m.nbFactures }}</span>
          <span class="tiny on-background-alt shrink-0" style="min-width: 70px; text-align: right; font-weight: 600">
            {{ m.puVenteMoyen | number:'1.2-2' }} €
          </span>
          <span class="tiny shrink-0" [class]="margeClass(m.tauxMarge)" style="min-width: 50px; text-align: right; font-weight: 600">
            {{ m.tauxMarge | number:'1.0-0' }}%
          </span>
          <span class="tiny on-disabled shrink-0" style="min-width: 80px; text-align: right">
            {{ m.ca | number:'1.0-0' }} €
          </span>
        </div>
      }
    }
  </div>
</div>
